import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ShieldCheck, Truck, Download } from "lucide-react";
import { http, euro, TYPE_LABEL, COLLECTION_LABEL } from "@/lib/api";
import { ProductAction, ProductCard } from "@/components/site/ProductCard";

const INFO = {
  physique: [Truck, "Expédié directement par notre fournisseur partenaire · Livraison offerte dès 60 €"],
  numerique: [Download, "Livraison numérique par email après confirmation du paiement"],
  affiliation: [ShieldCheck, "Vendu et livré par notre partenaire : prix, disponibilité, livraison et garantie sont indiqués sur son site."],
};

export default function ProductPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [all, setAll] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    setP(null);
    setError(false);
    http.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setError(true));
    http.get("/products").then((r) => setAll(Array.isArray(r.data) ? r.data : [])).catch(() => setAll([]));
  }, [id]);

  if (error) return <div className="px-5 pt-40 pb-20 text-center font-display text-3xl" data-testid="product-not-found">Produit introuvable.</div>;
  if (!p) return <div className="min-h-screen" />;
  const [Icon, info] = INFO[p.type] ?? INFO.physique;
  const related = all.filter((x) => x.collection === p.collection && x.id !== p.id).slice(0, 3);

  return (
    <div data-testid="product-detail-page" className="mx-auto max-w-[1440px] px-5 pt-[104px] sm:px-10">
      <Link to="/#catalogue" data-testid="back-to-shop-link" className="inline-flex items-center gap-2 text-sm text-[var(--muted)] hover:text-[var(--ink)]">
        <ArrowLeft className="h-4 w-4" /> Retour à la boutique
      </Link>
      <div className="mt-8 grid gap-12 lg:grid-cols-12">
        <motion.div initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7 overflow-hidden rounded-[24px] bg-[var(--bone-2)]">
          <img src={p.image} alt={p.name} className="aspect-[4/5] w-full object-cover sm:aspect-[5/4]" />
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }} className="lg:col-span-5 lg:pt-6">
          <p className="font-mono-label text-[11px] text-[var(--muted)]">{COLLECTION_LABEL[p.collection]} · {TYPE_LABEL[p.type]}</p>
          <h1 data-testid="product-detail-name" className="font-display mt-4 text-4xl sm:text-5xl font-light leading-[1.05] tracking-tight">{p.name}</h1>
          {p.price != null ? <p data-testid="product-detail-price" className="font-display mt-8 text-4xl">{euro(p.price)}</p>
            : <p data-testid="product-detail-price" className="mt-8 text-sm text-[var(--muted)]">Prix à jour affiché chez {p.partner}</p>}
          <h2 className="font-mono-label mt-8 text-[11px] text-[var(--muted)]">Les critères à vérifier</h2>
          <p className="mt-6 text-base leading-relaxed text-[var(--muted)]">{p.description}</p>
          <ul className="mt-8 space-y-3">
            {(p.benefits ?? []).map((b) => <li key={b} className="flex items-center gap-3 text-sm"><Check className="h-4 w-4 text-[var(--terra)]" />{b}</li>)}
          </ul>
          <div className="mt-10"><ProductAction p={p} full /></div>
          <p className="mt-5 flex items-start gap-3 rounded-2xl bg-[var(--bone-2)] p-4 text-sm"><Icon className="mt-0.5 h-4 w-4 shrink-0" />{info}</p>
        </motion.div>
      </div>
      {related.length > 0 && (
        <section className="py-24 sm:py-32">
          <h2 className="font-display mb-12 text-3xl sm:text-4xl font-light">Dans la même <em className="italic text-[var(--terra)]">collection</em></h2>
          <div className="grid grid-cols-1 gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r, i) => <ProductCard key={r.id} p={r} i={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
